import type {CallIntent, RoomCallSummary} from '@phonehq/rtc-core';
import {
    buildCallEscalationContent,
    MATRIX_CALL_ESCALATE_EVENT_TYPE,
    type ActiveDirectCall,
    type MatrixCallEscalationContent,
} from '../../../services/matrixCalling/callEscalation';
import type {CallSwitch, GroupCallJoinChoice, RingMembersOutcome} from '../../groupCalls/services/groupCallOrchestrator';
import type {MediaDevicePreferences} from '../../groupCalls/platform/WebRtcPlatform';

export const ESCALATION_JOIN_WAIT_MS = 10_000;
export const ESCALATION_IDLE_WAIT_MS = 3_000;
export const ESCALATION_PEER_GRACE_MS = 10_000;
export const ESCALATION_LEAD_WAIT_MS = 10_000;
const POLL_MS = 250;

export interface EscalationGroupPort {
    isBusy(): boolean;
    openLobby(roomId: string, intent: CallIntent): Promise<boolean>;
    join(choice: GroupCallJoinChoice): Promise<void>;
    ringMembers(targets: readonly string[]): Promise<RingMembersOutcome>;
    beginSwitch(switched: CallSwitch): void;
    finishSwitch(failed: boolean): void;
    isInCall(roomId: string): boolean;
    summary(roomId: string): RoomCallSummary | undefined;
}

export interface EscalationMedia {
    readonly microphoneEnabled: boolean;
    readonly cameraEnabled: boolean;
}

export interface CallEscalationDeps {
    readonly active: () => ActiveDirectCall | undefined;
    readonly localMedia: () => EscalationMedia;
    readonly localUserId: () => string | null;
    readonly memberName: (roomId: string, userId: string) => string;
    readonly roomName: (roomId: string) => string | undefined;
    readonly resolveMatrixId: (userId: number) => Promise<string | undefined>;
    readonly findOrCreateGroupRoom: (memberIds: readonly string[]) => Promise<string>;
    readonly isJoined: (roomId: string, userId: string) => boolean;
    readonly invitedBy: (roomId: string, userId: string) => string | undefined;
    readonly joinRoom: (roomId: string) => Promise<void>;
    readonly sendEvent: (roomId: string, type: string, content: MatrixCallEscalationContent) => Promise<void>;
    readonly endDirectCall: (matrixCallId: string) => Promise<void>;
    readonly group: EscalationGroupPort;
    readonly devices: () => MediaDevicePreferences;
    readonly now: () => number;
    readonly sleep: (ms: number) => Promise<void>;
    readonly schedule: (callback: () => void, ms: number) => void;
}

export interface EscalationPerson {
    readonly userId: number;
    readonly name: string;
}

export type EscalationOutcome = 'switched' | 'not_started' | 'failed';

const groupNameOf = (names: readonly string[]): string => names.filter(Boolean).join(', ');

export const createCallEscalation = (deps: CallEscalationDeps) => {
    const waitFor = async (condition: () => boolean, timeoutMs: number): Promise<boolean> => {
        const deadline = deps.now() + timeoutMs;
        while (!condition()) {
            if (deps.now() >= deadline) {
                return false;
            }
            await deps.sleep(POLL_MS);
        }
        return true;
    };

    const joinGroup = async (
        roomId: string,
        intent: CallIntent,
        media: EscalationMedia,
        extra: Pick<GroupCallJoinChoice, 'targets' | 'notify'>,
    ): Promise<boolean> => {
        await waitFor(() => !deps.group.isBusy(), ESCALATION_IDLE_WAIT_MS);
        if (!(await deps.group.openLobby(roomId, intent))) {
            return false;
        }
        await deps.group.join({
            intent,
            microphoneEnabled: media.microphoneEnabled,
            cameraEnabled: media.cameraEnabled,
            devices: deps.devices(),
            ...extra,
        });
        return deps.group.isInCall(roomId);
    };

    const ringPeerIfMissing = (roomId: string, peerId: string): void => {
        deps.schedule(() => {
            const summary = deps.group.summary(roomId);
            if (deps.group.isInCall(roomId) && !summary?.userIds.includes(peerId)) {
                void deps.group.ringMembers([peerId]);
            }
        }, ESCALATION_PEER_GRACE_MS);
    };

    const escalate = async (person: EscalationPerson, intent: CallIntent, expanded: boolean): Promise<EscalationOutcome> => {
        const active = deps.active();
        const me = deps.localUserId();
        const peerId = active?.remoteUserId;
        if (!active || !peerId || !me) {
            return 'not_started';
        }
        const peerName = deps.memberName(active.roomId, peerId);
        const newId = await deps.resolveMatrixId(person.userId).catch(() => undefined);
        if (!newId || newId === me || newId === peerId) {
            return 'not_started';
        }
        const media = deps.localMedia();
        deps.group.beginSwitch({from: peerName, to: groupNameOf([peerName, person.name]), roomId: '', intent, expanded});
        let roomId: string;
        try {
            roomId = await deps.findOrCreateGroupRoom([peerId, newId]);
            await waitFor(() => deps.isJoined(roomId, peerId) && deps.isJoined(roomId, newId), ESCALATION_JOIN_WAIT_MS);
            await deps.sendEvent(active.roomId, MATRIX_CALL_ESCALATE_EVENT_TYPE, buildCallEscalationContent({
                callId: active.matrixCallId,
                roomId,
                intent,
                now: deps.now(),
            }));
        } catch {
            deps.group.finishSwitch(false);
            return 'not_started';
        }
        try {
            await deps.endDirectCall(active.matrixCallId);
            const joined = await joinGroup(roomId, intent, media, {targets: [newId]});
            if (joined) {
                ringPeerIfMissing(roomId, peerId);
            }
            deps.group.finishSwitch(!joined);
            return joined ? 'switched' : 'failed';
        } catch {
            deps.group.finishSwitch(true);
            return 'failed';
        }
    };

    const fromSender = (roomId: string, me: string, senderId: string): boolean =>
        deps.isJoined(roomId, me) ? deps.isJoined(roomId, senderId) : deps.invitedBy(roomId, me) === senderId;

    const follow = async (content: MatrixCallEscalationContent, senderId: string, expanded: boolean): Promise<EscalationOutcome> => {
        const active = deps.active();
        const me = deps.localUserId();
        if (!active || !me || !fromSender(content.room_id, me, senderId)) {
            return 'not_started';
        }
        const media = deps.localMedia();
        const peerName = deps.memberName(active.roomId, senderId);
        deps.group.beginSwitch({
            from: peerName,
            to: deps.roomName(content.room_id) ?? peerName,
            roomId: content.room_id,
            intent: content.intent,
            expanded,
        });
        try {
            await deps.endDirectCall(active.matrixCallId);
            if (!deps.isJoined(content.room_id, me)) {
                await deps.joinRoom(content.room_id).catch(() => undefined);
                await waitFor(() => deps.isJoined(content.room_id, me), ESCALATION_JOIN_WAIT_MS);
            }
            const senderInCall = await waitFor(() => deps.group.summary(content.room_id)?.userIds.includes(senderId) ?? false, ESCALATION_LEAD_WAIT_MS);
            if (!senderInCall) {
                deps.group.finishSwitch(true);
                return 'failed';
            }
            const joined = await joinGroup(content.room_id, content.intent, media, {notify: 'none'});
            deps.group.finishSwitch(!joined);
            return joined ? 'switched' : 'failed';
        } catch {
            deps.group.finishSwitch(true);
            return 'failed';
        }
    };

    return {escalate, follow};
};
